// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * Click-to-define.
 *
 * The audience for this tool is a landlord at a kitchen table, an HR
 * co-ordinator, someone in accounts payable. They know what a payslip is and
 * they have never heard of a cross-reference table. Every term the interface
 * uses is defined here, in place, in a sentence — because a tool that shows
 * someone evidence in a vocabulary they cannot check is asking them to trust
 * it, and this one is trying very hard not to do that.
 */

export const GLOSSARY: Record<string, [string, string]> = {
  save: [
    'A save',
    'Saving a PDF again does not rewrite the file. It appends the changed parts to the end and leaves everything that was already there untouched — which is why the earlier version is still physically inside the bytes, and why it can be pulled back out.',
  ],
  xref: [
    'Cross-reference table',
    'The index at the end of a PDF saying where every object in it lives. Each save adds a new one that points back at the one before, and that chain of pointers is the document’s real history. peelback follows the chain rather than searching the file, because searching finds things that are not history.',
  ],
  eof: [
    'The %%EOF marker',
    'The three characters a PDF ends with. Articles about spotting edited PDFs often tell you to count them in a text editor. That count is usually wrong: web optimisation adds one near the start, an attached file brings its own along, and a page that happens to print the characters adds another.',
  ],
  linearized: [
    'Web-optimised',
    'A file arranged so a reader can display page one before the rest has arrived. It puts a second index near the start of the file, and a second %%EOF with it. Banks and payroll portals switch it on as a matter of course. It is not a save.',
  ],
  producer: [
    'Producer and Creator',
    'Two free-text labels an application stamps into a PDF when it writes one. /Creator names the program the document came from; /Producer names the thing that turned it into a PDF. Both say what wrote the file, never who — and re-saving in almost any reader replaces them.',
  ],
  xmp: [
    'XMP',
    'A block of metadata some applications embed. A few of them also keep a history inside it listing each save and what made it. Most files have no XMP at all, and most that do have no history — its absence means nothing.',
  ],
  bytrange: [
    'ByteRange',
    'A digital signature covers two spans of the file, written as four numbers. The space between them holds the signature’s own value, which could not sign itself. That space is not concealed content, and anything describing it as a gap is misreading it.',
  ],
  objstm: [
    'Object stream',
    'Modern PDFs compress most of their internals into one blob. It makes the file smaller and it makes the words “xref” and “trailer” vanish from the file entirely — so a reader that searches for those words reports a single save no matter how many there really were.',
  ],
  flatten: [
    'Flattening',
    'Re-saving a document so it becomes one new file with no history — printing it, exporting it, running it through almost any online PDF tool, or choosing “Save As” instead of “Save”. Anyone changing a document can do this by accident or on purpose, and afterwards there is nothing here to find.',
  ],
  redaction: [
    'Redaction',
    'Removing information before sharing a document. A black rectangle drawn over text and then saved is the commonest way it is done, and it is a save — so the version underneath the rectangle is still in the file. That is a real risk of using this tool on a document somebody sent you in confidence.',
  ],
};

let tip: HTMLElement | null = null;

function hide() {
  tip?.remove();
  tip = null;
}

function show(anchor: HTMLElement, key: string) {
  hide();
  const entry = GLOSSARY[key];
  if (!entry) return;

  const el = document.createElement('div');
  el.id = 'glossary-tip';
  el.setAttribute('role', 'tooltip');
  const strong = document.createElement('strong');
  strong.textContent = entry[0];
  el.append(strong, document.createTextNode(entry[1]));
  document.body.append(el);

  const rect = anchor.getBoundingClientRect();
  const box = el.getBoundingClientRect();
  const margin = 8;
  let left = rect.left;
  if (left + box.width > window.innerWidth - margin) left = window.innerWidth - box.width - margin;
  if (left < margin) left = margin;
  let top = rect.bottom + 6;
  if (top + box.height > window.innerHeight - margin) top = Math.max(margin, rect.top - box.height - 6);
  el.style.left = `${left}px`;
  el.style.top = `${top}px`;
  tip = el;
}

/** Wraps a term for use inside an innerHTML string. */
export function term(key: string, label?: string): string {
  const text = label ?? key;
  if (!GLOSSARY[key]) return text;
  return `<span class="glossary-link" data-term="${key}" role="button" tabindex="0">${text}</span>`;
}

export function initGlossary() {
  document.addEventListener('click', (event) => {
    const target = (event.target as HTMLElement | null)?.closest<HTMLElement>('.glossary-link');
    if (!target) {
      if (tip && !tip.contains(event.target as Node)) hide();
      return;
    }
    event.preventDefault();
    const key = target.dataset.term;
    if (key) show(target, key);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && tip) {
      // Only swallow the key when a tooltip is actually up, so Escape still
      // reaches whatever overlay is behind it.
      event.stopPropagation();
      hide();
      return;
    }
    const target = event.target as HTMLElement | null;
    if ((event.key === 'Enter' || event.key === ' ') && target?.classList.contains('glossary-link')) {
      event.preventDefault();
      const key = target.dataset.term;
      if (key) show(target, key);
    }
  });

  window.addEventListener('scroll', hide, { passive: true });
  window.addEventListener('resize', hide);
}
